// apps/tmg-web/components/ui/splash-button.tsx
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@workspace/ui/lib/utils";

// Tailored for SplashRides brand colors defined in your global CSS
const splashButtonVariants = cva(
  "inline-flex items-center justify-center rounded-xl text-sm font-semibold transition-colors tracking-wide focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        accent: "bg-accent text-accent-foreground hover:bg-accent/80",
        highlight:
          "bg-highlight text-highlight-foreground hover:bg-highlight/90",
        outline:
          "border border-input bg-transparent hover:bg-accent hover:text-accent-foreground",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "underline-offset-4 hover:underline text-primary",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 px-3 rounded-md",
        lg: "h-11 px-8 text-base",
        xl: "h-12 px-10 text-lg",
        "2xl": "h-14 px-12 text-xl",
        icon: "h-10 w-10 p-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface SplashButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof splashButtonVariants> {
  asChild?: boolean;
}

export const SplashButton = React.forwardRef<
  HTMLButtonElement,
  SplashButtonProps
>(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      className={cn(splashButtonVariants({ variant, size, className }))}
      ref={ref}
      {...props}
    />
  );
});

SplashButton.displayName = "SplashButton";

export { splashButtonVariants };
